import type {
  Country,
  CountryDetails,
  State,
  StateWithCountry,
  StateDetails,
  City,
  CityBasic,
  APIClientConfig,
  APIClient as IAPIClient
} from '../../types/location';
import { APIError } from '../../types/location';

/**
 * HTTP Client for location data via local Vercel serverless functions.
 * 
 * Data is served from the @countrystatecity/countries NPM package
 * running in serverless functions — no external API calls, no API key,
 * no rate limits.
 * 
 * Endpoints:
 *   GET /api/countries
 *   GET /api/states?country={iso2}
 *   GET /api/cities?country={iso2}&state={iso2}  (cities by state)
 *   GET /api/cities?country={iso2}                (all cities by country)
 */
export class CountryStateCityClient implements IAPIClient {
  private readonly baseURL: string;
  private readonly timeout: number;

  constructor(config: Partial<APIClientConfig> = {}) {
    this.baseURL = config.baseURL || '';
    this.timeout = config.timeout || 15000;
  }

  async fetchCountries(): Promise<Country[]> {
    const data = await this.request<Country[]>('/api/countries');
    return data.map(c => ({
      ...c,
      nombre_es: c.nombre_es || c.name,
    }));
  }

  async fetchCountryDetails(iso2: string): Promise<CountryDetails> {
    // Our local API doesn't have a dedicated details endpoint.
    // Return basic country info cast as CountryDetails.
    const countries = await this.fetchCountries();
    const country = countries.find(c => c.iso2 === iso2.toUpperCase());
    if (!country) {
      throw new APIError(`País no encontrado: ${iso2}`, 404, '/api/countries');
    }
    return country as unknown as CountryDetails;
  }

  async fetchStates(countryIso2: string): Promise<State[]> {
    const data = await this.request<Array<{ id: string; name: string; iso2: string }>>( 
      `/api/states?country=${countryIso2.toUpperCase()}`
    );
    return data.map(s => ({ id: s.id, name: s.name, iso2: s.iso2 }));
  }

  async fetchAllStates(query?: string): Promise<StateWithCountry[]> {
    const url = query ? `/api/all-states?q=${encodeURIComponent(query)}` : '/api/all-states';
    const data = await this.request<Array<{
      id: string; name: string; iso2: string;
      countryId: string; countryCode: string; countryName: string;
      type: string | null;
      latitude: string | null; longitude: string | null;
    }>>(url);

    return data.map(s => ({
      id: s.id,
      name: s.name,
      iso2: s.iso2,
      countryId: s.countryId,
      countryCode: s.countryCode,
      countryName: s.countryName,
      type: s.type || '',
      latitude: s.latitude ? Number.parseFloat(s.latitude) : 0,
      longitude: s.longitude ? Number.parseFloat(s.longitude) : 0,
    }));
  }

  async fetchStateDetails(countryIso2: string, stateIso2: string): Promise<StateDetails> {
    const states = await this.fetchStates(countryIso2);
    const state = states.find(s => s.iso2 === stateIso2.toUpperCase());
    if (!state) {
      throw new APIError(`Estado no encontrado: ${stateIso2}`, 404, '/api/states');
    }
    return state as unknown as StateDetails;
  }

  async fetchCities(countryIso2: string, stateIso2: string): Promise<City[]> {
    const data = await this.request<Array<{
      id: string; name: string; stateCode: string; countryCode: string;
      latitude: string; longitude: string;
    }>>(`/api/cities?country=${countryIso2.toUpperCase()}&state=${stateIso2.toUpperCase()}`);

    return data.map(c => ({
      id: c.id,
      name: c.name,
      stateId: '',
      stateCode: c.stateCode || stateIso2,
      countryId: '',
      countryCode: c.countryCode || countryIso2,
      latitude: c.latitude ? Number.parseFloat(c.latitude) : undefined,
      longitude: c.longitude ? Number.parseFloat(c.longitude) : undefined,
    }));
  }

  async fetchCitiesByCountry(countryIso2: string): Promise<CityBasic[]> {
    const data = await this.request<Array<{
      id: string; name: string; stateCode: string; countryCode: string;
      latitude: string; longitude: string;
    }>>(`/api/cities?country=${countryIso2.toUpperCase()}`);

    return data.map(c => ({
      id: c.id,
      name: c.name,
      stateCode: c.stateCode,
      countryCode: c.countryCode || countryIso2,
      latitude: c.latitude ? Number.parseFloat(c.latitude) : undefined,
      longitude: c.longitude ? Number.parseFloat(c.longitude) : undefined,
    }));
  }

  // ============================================================================
  // Private
  // ============================================================================

  private async request<T>(url: string): Promise<T> {
    const fullURL = `${this.baseURL}${url}`;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(fullURL, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new APIError(
          body || `Request failed with status ${response.status}`,
          response.status,
          url
        );
      }

      return await response.json() as T;
    } catch (error) {
      if (error instanceof APIError) throw error;

      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new APIError('Request timeout', 0, url);
      }

      throw new APIError(
        error instanceof Error ? error.message : 'Error de red',
        0,
        url
      );
    }
  }
}

/**
 * Singleton instance
 */
let clientInstance: CountryStateCityClient | null = null;

export function getAPIClient(config?: Partial<APIClientConfig>): CountryStateCityClient {
  clientInstance ??= new CountryStateCityClient(config);
  return clientInstance;
}

export function resetAPIClient(): void {
  clientInstance = null;
}
